import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges } from '@angular/core';
import { FileService } from '@app/core/services/file.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { DocArcGet, DocArcService, DocumentCategoryItem } from '@app/services/doc-arc.service';

@Component({
    selector: 'tt-doc-arc',
    templateUrl: './doc-arc.component.html',
    styleUrls: ['./doc-arc.component.css'],
    standalone: false,
})
export class DocArcComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: DocArcParams;

    public document?: DocArcGet;

    public documentCategories: DocumentCategoryItem[] = [];

    public selectedView: 'p' | 'i' = 'p';

    public canShowDocument = false;

    public loading = false;

    public saving = false;

    public deleting = false;

    private deregisterOfficeConversion?: () => void;

    private conversionTimeout?: number;

    constructor(
        private docArc: DocArcService,
        private file: FileService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
        @Optional() @Inject('$rootScope') private rootScope?: LegacyRootScope,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async load(): Promise<void> {
        const docarcKeyno = this.params?.docarc_keyno;

        if (docarcKeyno === undefined || docarcKeyno === null || `${docarcKeyno}` === '') return;

        try {
            this.loading = true;
            const [docArcDocument, documentCategories] = await Promise.all([this.docArc.getDocArc(docarcKeyno), this.docArc.getDocumentCategories()]);
            const fileDetails = await this.file.getFileDetails({ fileName: docArcDocument.docfilename, maxWidth: 1200, maxHeight: 600 });

            this.document = { ...docArcDocument, ...fileDetails };
            this.documentCategories = documentCategories;
            this.canShowDocument = this.canPreview(this.document);
        } finally {
            this.loading = false;
        }
    }

    public async save(): Promise<void> {
        if (!this.document) return;

        try {
            this.saving = true;
            this.document.wopiIsConverted = false;
            await this.docArc.saveDocArc(this.document);
            this.navigator?.back();
        } finally {
            this.saving = false;
        }
    }

    public async delete(): Promise<void> {
        const docarcKeyno = this.params?.docarc_keyno;

        if (docarcKeyno === undefined || docarcKeyno === null) return;

        try {
            this.deleting = true;
            await this.docArc.deleteDocArc(docarcKeyno);
            this.navigator?.back();
        } finally {
            this.deleting = false;
        }
    }

    public openDocument(): void {
        const imageLink = this.document?.imageLink;

        if (!imageLink) return;

        const prefix = window.navigator.userAgent.includes('gonative') ? 'external:' : '';
        const newWindow = window.open(`${prefix}${this.file.getTokenLink(imageLink)}`);

        newWindow?.focus();
    }

    public downloadDocument(): void {
        const imageLink = this.document?.imageLink;

        if (!imageLink) return;

        const target = window.navigator.userAgent.includes('gonative') ? '_blank' : '_self';
        window.open(this.file.getTokenLink(imageLink), target);
    }

    public ngOnInit(): void {
        if (this.navigator) {
            this.params = this.navigator.params as unknown as DocArcParams;
        }

        this.subscribeToOfficeConversion();
        void this.load();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']?.currentValue) {
            void this.load();
        }
    }

    public ngOnDestroy(): void {
        this.deregisterOfficeConversion?.();

        if (this.conversionTimeout !== undefined) {
            window.clearTimeout(this.conversionTimeout);
        }
    }

    private subscribeToOfficeConversion(): void {
        this.deregisterOfficeConversion = this.rootScope?.$on('ieOfficeOnline-onConvert', (_, data) => {
            void this.pollOfficeConversion({ ...data, fileItemKeyno: '0' });
        });
    }

    private async pollOfficeConversion(data: OfficeConversionEvent): Promise<void> {
        const result = await this.file.isConvertCompleted({ fileItemId: data.fileItemId, fileItemKeyno: data.fileItemKeyno ?? '0' });

        if (result.isCompleted) {
            if (this.document) {
                await this.docArc.updateDocArcFileName({ docarcKeyno: this.document.docarc_keyno, docFileName: result.fileName });
            }

            return;
        }

        this.conversionTimeout = window.setTimeout(() => void this.pollOfficeConversion({ ...data, fileItemKeyno: result.fileItemKeyno }), 200);
    }

    private canPreview(document: DocArcGet): boolean {
        const contentType = document.contentType ?? '';

        if (this.file.isOfficeContent(contentType)) return true;

        const localFileName = document.localFileName ?? '';

        if (localFileName.toLowerCase().endsWith('.pdf')) return true;

        const fileExtension = localFileName.split('.').pop()?.toLowerCase() ?? '';
        const thumbnailFileName = (document.thumbnailLink ?? '').split('/').pop() ?? '';
        const thumbnailNameWithoutExtension = thumbnailFileName.split('.').slice(0, -1).join('.').toLowerCase();

        return thumbnailNameWithoutExtension !== fileExtension;
    }
}

export interface DocArcParams {
    docarc_keyno: string | number;
}

interface OfficeConversionEvent {
    fileItemId: string;
    fileItemKeyno?: string;
}

interface LegacyRootScope {
    $on(eventName: string, listener: (event: unknown, data: OfficeConversionEvent) => void): () => void;
}
